import { Plus } from "lucide-react";
import { useState, type FormEvent } from "react";
import { ChapterPicker } from "@/book/components/chapter-picker";
import { ThankYou } from "@/book/components/thank-you";
import { errorMessage } from "@/shared/api/client";
import { useChapters, useErrata, useSubmitErratum } from "@/shared/api/book";
import { num, useFilters } from "@/shared/lib/use-filters";
import { Button } from "@/shared/ui/button";
import { Chip } from "@/shared/ui/chip";
import { SearchInput, Select } from "@/shared/ui/filters";
import { Input, Textarea } from "@/shared/ui/input";
import { Label } from "@/shared/ui/label";
import { Markdown } from "@/shared/ui/markdown";
import { PageTitle } from "@/shared/ui/page-header";
import { Sheet } from "@/shared/ui/sheet";
import { Empty, ErrorNote, ListSkeleton, Pager, StatusChip } from "@/shared/ui/state";

const DEFAULTS = { q: "", chapter_id: "", page: "1" };

/** Hibajegyzék / „amit ma másképp látunk" — tőlünk származó javítások és olvasói beküldések. */
export function ErrataPage() {
  const { values, set } = useFilters(DEFAULTS);
  const { data: chapters } = useChapters();
  const [open, setOpen] = useState(false);
  const { data, isPending, isError } = useErrata({
    q: values.q,
    chapter_id: num(values.chapter_id),
    page: Number(values.page) || 1,
  });

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between gap-3">
        <PageTitle>Hibajegyzék</PageTitle>
        <Button size="sm" variant="outline" onClick={() => setOpen(true)}>
          <Plus size={16} />
          Beküldés
        </Button>
      </div>
      <p className="-mt-2 text-[15px] leading-[1.5] text-muted">
        Amit azóta kijavítanánk vagy ma másképp látunk a könyvben. Ha te is találsz ilyet,
        küldd be.
      </p>

      <SearchInput
        label="Keresés a hibajegyzékben"
        placeholder="Keresés a hibajegyzékben"
        value={values.q}
        onChange={(q) => set({ q })}
      />

      {chapters && chapters.length > 0 && (
        <Select
          aria-label="Fejezet"
          value={values.chapter_id}
          onChange={(e) => set({ chapter_id: e.target.value })}
        >
          <option value="">Minden fejezet</option>
          {chapters.flatMap((c) => [c, ...c.children]).map((c) => (
            <option key={c.id} value={c.id}>
              {c.number}. {c.title}
            </option>
          ))}
        </Select>
      )}

      {isPending && <ListSkeleton />}
      {isError && <ErrorNote />}
      {data &&
        (data.items.length === 0 ? (
          <Empty
            action={
              <Button variant="outline" onClick={() => setOpen(true)}>
                Beküldök egy meglátást
              </Button>
            }
          >
            Egyelőre nincs javítanivaló a listán.
          </Empty>
        ) : (
          <div className="flex flex-col">
            {data.items.map((e) => (
              <article key={e.id} className="flex flex-col gap-1.5 border-t border-line py-3">
                <div className="flex flex-wrap items-center gap-1.5">
                  {e.chapters.map((c) => (
                    <Chip key={c.id}>{c.number}. fejezet</Chip>
                  ))}
                  {e.locator && <Chip variant="neutral">{e.locator}</Chip>}
                  {!e.from_authors && <Chip variant="neutral">Olvasói meglátás</Chip>}
                  <StatusChip status={e.status} />
                </div>
                <Markdown className="text-[15px]">{e.body}</Markdown>
              </article>
            ))}
          </div>
        ))}
      {data && (
        <Pager
          page={data.page}
          total={data.total}
          pageSize={data.page_size}
          onChange={(page) => set({ page: String(page) })}
        />
      )}

      {/* Nincs „jóváhagyás után megjelenik" ígéret: a beküldést a szerzők kézzel dolgozzák
          fel, nem kerül ki változtatás nélkül. */}
      <Sheet open={open} onOpenChange={setOpen} title="Meglátás beküldése">
        <ErratumForm onDone={() => setOpen(false)} />
      </Sheet>
    </div>
  );
}

function ErratumForm({ onDone }: { onDone: () => void }) {
  const { data: chapters } = useChapters();
  const submit = useSubmitErratum();
  const [body, setBody] = useState("");
  const [locator, setLocator] = useState("");
  const [chapterIds, setChapterIds] = useState<number[]>([]);
  const [sent, setSent] = useState(false);

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    submit.mutate(
      {
        body: body.trim(),
        locator: locator.trim() || null,
        chapter_ids: chapterIds,
      },
      { onSuccess: () => setSent(true) },
    );
  };

  // Sikeres beküldés után a panel nem záródik be szó nélkül: köszönet + mi történik ezután.
  if (sent) {
    return (
      <ThankYou title="Köszönjük a beküldést!" onDone={onDone}>
        A meglátásodat a szerzők átnézik, és ha helytálló, a javítás bekerül a hibajegyzékbe.
      </ThankYou>
    );
  }

  return (
    <form onSubmit={onSubmit} className="flex flex-col gap-4">
      <div className="flex flex-col gap-2">
        <Label htmlFor="e-body">Milyen hibát találtál?</Label>
        <Textarea
          id="e-body"
          rows={5}
          required
          minLength={10}
          value={body}
          onChange={(e) => setBody(e.target.value)}
        />
      </div>
      <div className="flex flex-col gap-2">
        <Label htmlFor="e-locator">Hol találtad?</Label>
        <Input
          id="e-locator"
          placeholder="pl. 47. oldal, 2. bekezdés"
          value={locator}
          onChange={(e) => setLocator(e.target.value)}
        />
      </div>
      {chapters && chapters.length > 0 && (
        <ChapterPicker
          label="Melyik fejezet(ek)hez tartozik?"
          chapters={chapters}
          value={chapterIds}
          onChange={setChapterIds}
        />
      )}
      {submit.isError && <ErrorNote>{errorMessage(submit.error)}</ErrorNote>}
      <Button type="submit" block size="lg" disabled={submit.isPending || body.trim().length < 10}>
        {submit.isPending ? "Küldés…" : "Beküldöm"}
      </Button>
    </form>
  );
}

